import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AuthStore from "../../../stores/AuthStore";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Timeline } from "@/components/dashboard/timeline";
import { TaskCard } from "@/components/tasks/task-card";
import { CreateTaskDialog } from "@/components/tasks/create-task-dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getTasks, updateTask } from "@/data/requests/task-requests";
import { getHabits } from "@/data/requests/habit-requests";
import { format, isSameDay, parseISO } from "date-fns";
import { Plus, Zap, CheckCircle2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { PageLoader } from "@/components/shared/page-loader";
import { useNavigate } from "react-router-dom";

export function HomeScreen() {
  const { t } = useTranslation();
  const user = AuthStore.user;
  const [selectedDate, setSelectedDate] = useState(new Date());
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch tasks
  const { data: tasksResponse, isLoading } = useQuery({
    queryKey: ["tasks", debouncedSearch],
    queryFn: () => getTasks({ search: debouncedSearch || undefined }),
  });

  const tasksData = tasksResponse?.data?.data || tasksResponse?.data || [];
  const tasks = Array.isArray(tasksData) ? tasksData : [];

  // Fetch habits
  const { data: habitsResponse } = useQuery({
    queryKey: ["habits"],
    queryFn: getHabits,
  });

  const habitsData = habitsResponse?.data?.data || habitsResponse?.data || [];
  const habits = Array.isArray(habitsData) ? habitsData : [];

  // Helper: Only show habit on or after its creation date
  const isOnOrAfterCreationDate = (habit, date) => {
    if (!habit.createdAt) return true;
    try {
      const createdAt =
        typeof habit.createdAt === "string"
          ? parseISO(habit.createdAt)
          : new Date(habit.createdAt);
      const createdAtDay = new Date(createdAt);
      const targetDay = new Date(date);
      createdAtDay.setHours(0, 0, 0, 0);
      targetDay.setHours(0, 0, 0, 0);
      return targetDay >= createdAtDay;
    } catch {
      return true;
    }
  };

  // Filter habits for selected date (habits with reminderTime)
  const todaysHabits = habits.filter((habit) => {
    if (!habit.reminderTime) return false;
    if (!isOnOrAfterCreationDate(habit, selectedDate)) return false;
    const dayOfWeek = selectedDate.getDay();
    if (habit.cadence === "weekdays" && (dayOfWeek === 0 || dayOfWeek === 6))
      return false;
    if (habit.cadence === "weekends" && dayOfWeek !== 0 && dayOfWeek !== 6)
      return false;
    return true;
  });

  // Filter tasks for selected date
  const todaysTasks = tasks.filter((task) => {
    if (!task.dueDate) return false;
    return isSameDay(new Date(task.dueDate), selectedDate);
  });

  // Urgent & Important (Do First)
  const urgentTasks = todaysTasks.filter(
    (t) => t.isUrgent && t.isImportant && !t.isCompleted
  );

  // Other tasks for the day
  const otherTasks = todaysTasks.filter(
    (t) => !(t.isUrgent && t.isImportant) && !t.isCompleted
  );

  // Completed tasks
  const completedTasks = todaysTasks.filter((t) => t.isCompleted);

  // Toggle Complete Mutation
  const toggleCompleteMutation = useMutation({
    mutationFn: ({ id, isCompleted }) => updateTask(id, { isCompleted }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
    onError: () => {
      toast({
        title: t("common.error"),
        description: t("home.taskStatusUpdateFailed"),
        variant: "destructive",
      });
    },
  });

  const handleToggleComplete = (id, isCompleted) => {
    toggleCompleteMutation.mutate({ id, isCompleted });
  };

  const handleTaskClick = (task) => {
    if (!task?.id) return;
    navigate(`/tasks/${task.id}`);
  };

  if (isLoading) {
    return (
      <AppLayout title={t("home.title")} description={t("home.loadingDay")}>
        <PageLoader />
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={t("home.title")}
      description={t("home.greeting", {
        name: user?.name || t("profile.user"),
      })}
    >
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("home.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {debouncedSearch ? (
          <section className="space-y-3">
            <h3 className="font-semibold text-lg">
              {t("home.searchResultsTitle")}
            </h3>
            {tasks.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {t("home.noTasksFound", { query: debouncedSearch })}
              </p>
            ) : (
              <div className="space-y-3">
                {tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onToggleComplete={handleToggleComplete}
                    onClick={handleTaskClick}
                  />
                ))}
              </div>
            )}
          </section>
        ) : (
          <>
            {/* Timeline */}
            <Timeline
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
            />

            {/* Habits Section */}
            <section>
              <h3 className="font-semibold text-lg mb-3">
                {t("home.todaysHabits", { count: todaysHabits.length })}
              </h3>
              {todaysHabits.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-6 text-center">
                    <p className="text-sm text-muted-foreground">
                      {habits.length === 0
                        ? t("home.noHabitsYet")
                        : t("home.noHabitsToday")}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {todaysHabits
                    .sort((a, b) => {
                      if (!a.reminderTime || !b.reminderTime) return 0;
                      return a.reminderTime.localeCompare(b.reminderTime);
                    })
                    .map((habit) => (
                      <Card
                        key={habit.id}
                        className="hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/habits/${habit.id}`)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0">
                              <div className="text-lg font-bold text-primary">
                                {habit.reminderTime}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-base truncate">
                                {habit.name}
                              </h4>
                              {habit.description && (
                                <p className="text-sm text-muted-foreground truncate">
                                  {habit.description}
                                </p>
                              )}
                            </div>
                            <div className="flex-shrink-0">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                {t("home.habit")}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              )}
            </section>

            {/* Urgent Section */}
            {urgentTasks.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3 text-red-500 font-semibold">
                  <Zap className="h-5 w-5 fill-current" />
                  <h3>{t("home.doFirst")}</h3>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {urgentTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onToggleComplete={handleToggleComplete}
                      onClick={handleTaskClick}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Main Task List */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-lg">
                  {t("home.planFor", {
                    date: format(selectedDate, "dd.MM.yyyy"),
                  })}
                </h3>
                <CreateTaskDialog defaultDate={selectedDate}>
                  <Button size="sm" variant="outline" id="create-task-trigger">
                    <Plus className="h-4 w-4 mr-1" /> {t("home.addTask")}
                  </Button>
                </CreateTaskDialog>
              </div>

              {otherTasks.length === 0 &&
              urgentTasks.length === 0 &&
              completedTasks.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-10 text-center">
                    <div className="rounded-full bg-primary/10 p-3 mb-3">
                      <CheckCircle2 className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-medium text-lg">
                      {t("home.noTasksToday")}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t("home.noTasksTodayDesc")}
                    </p>
                    <CreateTaskDialog defaultDate={selectedDate}>
                      <Button>{t("home.createFirstTask")}</Button>
                    </CreateTaskDialog>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {otherTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onToggleComplete={handleToggleComplete}
                      onClick={handleTaskClick}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Completed Section */}
            {completedTasks.length > 0 && (
              <section className="pt-4 border-t">
                <h3 className="font-medium text-muted-foreground mb-3 text-sm">
                  {t("home.completed")}
                </h3>
                <div className="space-y-2 opacity-60">
                  {completedTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onToggleComplete={handleToggleComplete}
                      onClick={handleTaskClick}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
